import React from "react";
import { format } from "date-fns";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fakeUsers, MyLatestReminderList } from "@/views/home/calendars/mocks/calendarMocks";

export interface EventTableProps {
  eventsAndReminders: MyLatestReminderList["reminderList"];
  limit?: number;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const reminderList: MyLatestReminderList["reminderList"] = [
  {
    id: "e1",
    title: "Abacus Delta LtD vs Clover Offices Ltd: Court Hearing",
    datetime: "2024-06-26T09:00:00Z",
    matterId: "ABC123",
    party: "Claimant",
    type: "event",
    summary: "Important court hearing for case ABC123",
    documentId: "doc1",
    calendarReminder: true,
    smsReminder: true,
    assignedUserIds: ["2"],
    location: "London Court of Appeal",
  },
  {
    id: "r1",
    title: "2 days before Court Hearing Abacus Delta LtD vs Clover Offices Ltd",
    datetime: "2024-06-27T10:00:00Z",
    matterId: "ABC123",
    party: "Claimant",
    type: "reminder",
    summary: "Reminder: Court hearing in 2 days",
    documentId: "doc1",
    calendarReminder: true,
    smsReminder: true,
    assignedUserIds: ["1", "2"],
    location: "London Court of Appeal",
  },
  {
    id: "e2",
    title: "Document Submission Deadline Abacus Delta LtD vs Clover Offices Ltd",
    datetime: "2024-06-28T13:00:00Z",
    matterId: "XYZ456",
    party: "Defendant",
    type: "event",
    summary: "Deadline for submitting documents for case XYZ456",
    documentId: "doc2",
    calendarReminder: true,
    smsReminder: false,
    assignedUserIds: ["1", "2"],
    location: "Office",
  },
  {
    id: "r2",
    title: "1 day before Document Submission Abacus Delta LtD vs Clover Offices Ltd",
    datetime: "2024-07-01T13:00:00Z",
    matterId: "XYZ456",
    party: "Defendant",
    type: "reminder",
    summary: "Reminder: Document submission in 1 day",
    documentId: "doc2",
    calendarReminder: true,
    smsReminder: false,
    assignedUserIds: ["2", "3"],
    location: "Office",
  },
  {
    id: "r2a",
    title: "1 day before witness statement Abacus Delta LtD vs Clover Offices Ltd",
    datetime: "2024-07-02T14:00:00Z",
    matterId: "XYZ456",
    party: "Defendant",
    type: "reminder",
    summary: "Reminder: Document submission in 1 day",
    documentId: "doc2",
    calendarReminder: true,
    smsReminder: false,
    assignedUserIds: ["3"],
    location: "Office",
  },
  {
    id: "r2b",
    title: "1 day before Document Submission",
    datetime: "2024-07-03T14:00:00Z",
    matterId: "XYZ456",
    party: "Defendant",
    type: "reminder",
    summary: "Reminder: Document submission in 1 day",
    documentId: "doc2",
    calendarReminder: true,
    smsReminder: false,
    assignedUserIds: ["2", "3"],
    location: "Office",
  },
  {
    id: "r2c",
    title: "1 day before Document Submission Abacus Delta Ltd",
    datetime: "2024-07-14T14:00:00Z",
    matterId: "XYZ456",
    party: "Defendant",
    type: "reminder",
    summary: "Reminder: Document submission in 1 day",
    documentId: "doc2",
    calendarReminder: true,
    smsReminder: false,
    assignedUserIds: ["1", "2"],
    location: "Office",
  },
  {
    id: "e3",
    title: "Epic Games vs Alphabet LLC: Initial Witness Meeting",
    datetime: "2024-07-16T10:00:00Z",
    matterId: "DEF789",
    party: "All Parties",
    type: "event",
    summary: "First meeting with new client for case DEF789",
    documentId: "doc3",
    calendarReminder: true,
    smsReminder: true,
    assignedUserIds: ["1"],
    location: "Office",
  },
];

export const DashboardEventsTable: React.FC<EventTableProps> = ({ limit }) => {
  const sortedEventsAndReminders = reminderList;

  const itemsToDisplay = limit ? sortedEventsAndReminders.slice(0, limit) : sortedEventsAndReminders;

  const renderRow = (item: MyLatestReminderList["reminderList"][0]) => {
    const owner = item.assignedUserIds.length > 0 ? fakeUsers.find(u => u.id === item.assignedUserIds[0]) : null;

    return (
      <TableRow
        key={item.id}
        className={classNames(
          item.type === "event" ? "bg-ivory-mist-200 bg-opacity-50" : "bg-desert-sand-100 bg-opacity-60",
        )}
      >
        <TableCell className="px-4 py-6 text-ui-heading-text">
          {format(new Date(item.datetime), "dd/MM/yyyy, EEEE")}
          <br />
          {owner && (
            <div className="mt-2 flex items-center">
              <img
                className="inline-block h-6 w-6 rounded-full ring-2 ring-white"
                src={owner.avatar}
                alt={owner.name}
                width={32}
                height={32}
              />
              <span className="ml-2 text-sm text-ui-heading-text">{owner.name}</span>
            </div>
          )}
        </TableCell>
        <TableCell className="px-4 py-4 text-ui-heading-text">
          {item.title} <span className="font-semibold">{`by ${format(new Date(item.datetime), "h:mmaaa")}`}</span>
          <br />
          <span className="text-xs text-ui-heading-text">{item.summary}</span>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Table className="min-w-full bg-white">
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/3 px-4 py-2">Date and Owner</TableHead>
          <TableHead className="w-2/3 px-4 py-2">Event Details</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{itemsToDisplay.map(item => renderRow(item))}</TableBody>
    </Table>
  );
};
